<template>
  <div class="form-section">
    <h4 class="text-responsive-lg font-bold text-center mb-responsive text-ietextlight">
      Select Changes Needed
    </h4>
    <p class="text-center text-sm text-gray-600 mb-4">Check all that apply.</p>
    
    <!-- Add validation message area for checkbox selection -->
    <div v-if="errors.changeSelection" class="text-red-500 text-xs italic text-center mb-4">
      {{ errors.changeSelection }}
    </div>
    
    <div class="space-y-responsive">
      <!-- Checkbox Group -->
      <div class="form-grid-2 md:form-grid-3 gap-4 mb-6">
        <!-- Consolidated Coverage Checkbox -->
        <label 
          class="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors" 
          :class="{'bg-blue-50 border-blue-300': modelValue.updateCoveragesChecked}"
        >
          <input 
            type="checkbox" 
            :value="modelValue.updateCoveragesChecked"
            @input="handleCheckboxChange('updateCoveragesChecked', $event.target.checked)"
            :class="[customerType === 'customer' ? 'enhanced-checkbox-customer' : 'enhanced-checkbox', 'h-5 w-5']"
          >
          <Icon name="mdi:cash-multiple" class="w-6 h-6 mx-2"></Icon>
          <span class="text-responsive-sm font-medium">Update Coverages/Deductible</span>
        </label>
        
        <!-- Add Insured Checkbox -->
        <label 
          class="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors" 
          :class="{'bg-blue-50 border-blue-300': modelValue.addInsuredChecked}"
        >
          <input 
            type="checkbox"
            :value="modelValue.addInsuredChecked"
            @input="handleCheckboxChange('addInsuredChecked', $event.target.checked)"
            :class="[customerType === 'customer' ? 'enhanced-checkbox-customer' : 'enhanced-checkbox', 'h-5 w-5']"
          >
          <Icon name="material-symbols:person-add-outline" class="w-6 h-6 mx-2"></Icon>
          <span class="text-responsive-sm font-medium">Add Insured</span>
        </label>
        
        <!-- Remove Insured Checkbox -->
        <label 
          class="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors" 
          :class="{'bg-blue-50 border-blue-300': modelValue.removeInsuredChecked}"
        >
          <input 
            type="checkbox"
            :value="modelValue.removeInsuredChecked"
            @input="handleCheckboxChange('removeInsuredChecked', $event.target.checked)"
            :class="[customerType === 'customer' ? 'enhanced-checkbox-customer' : 'enhanced-checkbox', 'h-5 w-5']"
          >
          <Icon name="material-symbols:person-remove-outline" class="w-6 h-6 mx-2"></Icon>
          <span class="text-responsive-sm font-medium">Remove Insured</span>
        </label>
      </div>

      <!-- Conditionally Displayed Input Sections -->
      <div class="space-y-6 border-t pt-6 mt-6">
        <UpdateCoveragesSection
          v-if="modelValue.updateCoveragesChecked"
          :modelValue="modelValue.changeDetails.coveragesData"
          @update:modelValue="updateModelValue({ changeDetails: { ...modelValue.changeDetails, coveragesData: $event } })"
          ref="updateCoveragesSectionRef"
        />
        
        <AddInsuredSection
          v-if="modelValue.addInsuredChecked"
          :modelValue="modelValue.changeDetails.addInsuredData"
          @update:modelValue="updateModelValue({ changeDetails: { ...modelValue.changeDetails, addInsuredData: $event } })"
          ref="addInsuredSectionRef"
        />
        
        <RemoveInsuredSection
          v-if="modelValue.removeInsuredChecked"
          :modelValue="modelValue.changeDetails.removeInsuredData"
          @update:modelValue="updateModelValue({ changeDetails: { ...modelValue.changeDetails, removeInsuredData: $event } })"
          ref="removeInsuredSectionRef"
        />
      </div>
    </div>

    <div class="flex-responsive-col justify-between mt-responsive">
      <button
        @click="$emit('previous')"
        class="btn-responsive-secondary mb-2 sm:mb-0"
      >
        Back
      </button>
      <button
        @click="$emit('next')"
        :class="customerType === 'lender' ? 'btn-lender' : 'btn-customer'"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
// Vue core imports
import { ref, onMounted, watch } from 'vue';

// UI components
import Icon from '@/components/common/Icon.vue';

// Section components
import UpdateCoveragesSection from '@/components/customerservice/policy-management/forms/home/steps/sections/UpdateCoveragesSection.vue';
import AddInsuredSection from '@/components/customerservice/policy-management/forms/home/steps/sections/AddInsuredSection.vue';
import RemoveInsuredSection from '@/components/customerservice/policy-management/forms/home/steps/sections/RemoveInsuredSection.vue';

export default {
  name: 'ChangesSelectionStep',
  components: {
    Icon,
    UpdateCoveragesSection,
    AddInsuredSection,
    RemoveInsuredSection
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
      default: () => ({
        updateCoveragesChecked: false,
        addInsuredChecked: false,
        removeInsuredChecked: false,
        changeDetails: {
          coveragesData: {},
          addInsuredData: {},
          removeInsuredData: {}
        }
      })
    },
    customerType: {
      type: String,
      default: 'customer'
    },
    errors: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'update:modelValue',
    'previous',
    'next',
    'update:errors'
  ],
  setup(props, { emit }) {
    // Component refs
    const updateCoveragesSectionRef = ref(null);
    const addInsuredSectionRef = ref(null);
    const removeInsuredSectionRef = ref(null);
    
    // Local model value
    const localModelValue = ref({ ...props.modelValue });

    // Validation method
    const validate = async () => {
      let isValid = true;
      let sectionErrors = {};

      // First check if at least one change is selected
      if (!localModelValue.value?.updateCoveragesChecked && 
          !localModelValue.value?.addInsuredChecked && 
          !localModelValue.value?.removeInsuredChecked) {
        emit('update:errors', { ...props.errors, changeSelection: 'Please select at least one change' });
        return false; 
      }
      // Clear general error if proceeding
      emit('update:errors', { ...props.errors, changeSelection: null });
      
      // Validate each selected section
      if (localModelValue.value?.updateCoveragesChecked) {
        if (updateCoveragesSectionRef.value?.validate) {
          const sectionValid = await updateCoveragesSectionRef.value.validate();
          if (!sectionValid) {
            isValid = false;
          }
        }
      }
      
      if (localModelValue.value?.addInsuredChecked) {
        if (addInsuredSectionRef.value?.validate) {
          const sectionValid = await addInsuredSectionRef.value.validate();
          if (!sectionValid) isValid = false;
        }
      }
      
      if (localModelValue.value?.removeInsuredChecked) {
        if (removeInsuredSectionRef.value?.validate) {
          const sectionValid = await removeInsuredSectionRef.value.validate();
          if (!sectionValid) isValid = false;
        }
      }
      
      // If any section is invalid, you might want to emit more specific errors here
      if (!isValid && !props.errors.changeSelection) {
          emit('update:errors', { ...props.errors, sectionDetailError: 'Please correct errors in the highlighted sections.' });
      } else if (isValid) {
          emit('update:errors', { ...props.errors, sectionDetailError: null });
      }

      return isValid;
    };

    // Initialize the modelValue structure if it doesn't exist
    const initializeModelValue = () => {
      const currentDetails = localModelValue.value?.changeDetails;
      if (!currentDetails || 
          !currentDetails.coveragesData || 
          !currentDetails.addInsuredData || 
          !currentDetails.removeInsuredData) { // Basic check, can be more specific
        
        const newValue = {
          ...(localModelValue.value || {}),
          changeDetails: {
            coveragesData: (currentDetails?.coveragesData && Object.keys(currentDetails.coveragesData).length > 0) ? currentDetails.coveragesData : { 
              dwellingAmount: 300000, 
              personalPropertyAmount: 150000, 
              deductibleAmount: 2000 
            },
            addInsuredData: currentDetails?.addInsuredData || { /* default structure */ },
            removeInsuredData: currentDetails?.removeInsuredData || { /* default structure */ }
          }
        };
        // Update localModelValue directly first before emitting to avoid race conditions
        localModelValue.value = newValue;
        emit('update:modelValue', newValue);
      }
    };

    // Call initialization on component mount
    onMounted(() => {
      initializeModelValue();
    });

    // Watch handler
    watch(() => props.modelValue, (newVal) => {
      if (JSON.stringify(localModelValue.value) !== JSON.stringify(newVal)) {
        localModelValue.value = { ...newVal };
      }
    }, { deep: true });

    // Update the updateModelValue method
    const updateModelValue = (updates) => {
      const newValue = {
        ...(localModelValue.value || {}),
        ...updates,
        changeDetails: {
          ...(localModelValue.value?.changeDetails || {}),
          ...(updates.changeDetails || {})
        }
      };
      
      // Only emit if the value has actually changed
      if (JSON.stringify(localModelValue.value) !== JSON.stringify(newValue)) {
        localModelValue.value = newValue;
        emit('update:modelValue', newValue);
      }
    };

    // Update the checkbox handlers
    const handleCheckboxChange = (type, value) => {
      let detailData = {};
      if (value) {
        // Initialize with default structure if checking the box
        if (type === 'updateCoveragesChecked') {
          detailData = { dwellingAmount: 300000, personalPropertyAmount: 150000, deductibleAmount: 2000 };
        } else if (type === 'addInsuredChecked') {
          // Define default structure for addInsuredData if needed
          detailData = { /* e.g., name: '', dob: null */ }; 
        } else if (type === 'removeInsuredChecked') {
          // Define default structure for removeInsuredData if needed
          detailData = { /* e.g., insuredId: null */ }; 
        }
      } else {
        detailData = undefined; // or {} depending on how you want to clear it
      }

      const updates = {
        [type]: value,
        changeDetails: {
          ...(localModelValue.value?.changeDetails || {}),
          [type.replace('Checked', 'Data')]: detailData
        }
      };
      updateModelValue(updates);
    };
    
    return {
      // Component refs
      updateCoveragesSectionRef,
      addInsuredSectionRef,
      removeInsuredSectionRef,
      
      // Methods
      validate,
      updateModelValue,
      handleCheckboxChange
    };
  }
};
</script>

<style scoped>
.form-grid-2 {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.form-grid-3 {
  @apply grid grid-cols-1 md:grid-cols-3 gap-4;
}

.flex-responsive-col {
  @apply flex flex-col sm:flex-row sm:justify-between;
}
</style> 